import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

export default function StudioSocialDashboard() {
  const { studioId, gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [leaderboards, setLeaderboards] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [antiCheatFlags, setAntiCheatFlags] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [achievementStats, setAchievementStats] = useState({
    totalAchievements: 0,
    totalUnlocks: 0,
    averageUnlockRate: 0,
    mostPopular: null,
    rarest: null
  });
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    totalPlayers: 0,
    activeTournaments: 0,
    flaggedEntries: 0
  });
  const [activeTab, setActiveTab] = useState('overview'); // overview, leaderboards, tournaments, achievements, anticheat

  useEffect(() => {
    if (user && studioId) {
      loadStudioData();
    }
  }, [user, studioId, gameId]);

  const loadStudioData = async () => {
    try {
      setLoading(true);

      // Load studio
      const { data: studioData, error: studioError } = await supabase
        .from('gaming_studios')
        .select('*')
        .eq('id', studioId)
        .eq('owner_id', user.id)
        .single();

      if (studioError) throw studioError;
      setStudio(studioData);

      // Load games
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .eq('studio_id', studioId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (gamesError) throw gamesError;
      setGames(gamesData);

      // Auto-select game if gameId in URL or select first game
      const gameToSelect = gameId
        ? gamesData.find(g => g.id === gameId)
        : gamesData[0];

      if (gameToSelect) {
        setSelectedGame(gameToSelect);
        await loadGameSocialData(gameToSelect.id);
      }
    } catch (error) {
      console.error('Error loading studio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameSocialData = async (gameIdToLoad) => {
    try {
      // Load leaderboards
      const { data: leaderboardsData, error: leaderboardsError } = await supabase
        .from('leaderboards')
        .select('*')
        .eq('game_id', gameIdToLoad)
        .order('created_at', { ascending: false });

      if (leaderboardsError) throw leaderboardsError;
      setLeaderboards(leaderboardsData || []);

      // Load leaderboard stats
      if (leaderboardsData && leaderboardsData.length > 0) {
        const leaderboardIds = leaderboardsData.map(lb => lb.id);

        const { count: submissionCount } = await supabase
          .from('leaderboard_entries')
          .select('id', { count: 'exact', head: true })
          .in('leaderboard_id', leaderboardIds);

        const { data: uniquePlayers } = await supabase
          .from('leaderboard_entries')
          .select('user_id')
          .in('leaderboard_id', leaderboardIds);

        const uniquePlayerCount = uniquePlayers
          ? new Set(uniquePlayers.map(p => p.user_id)).size
          : 0;

        const { count: flaggedCount } = await supabase
          .from('leaderboard_entries')
          .select('id', { count: 'exact', head: true })
          .in('leaderboard_id', leaderboardIds)
          .eq('flagged_for_review', true);

        setStats(prev => ({
          ...prev,
          totalSubmissions: submissionCount || 0,
          totalPlayers: uniquePlayerCount,
          flaggedEntries: flaggedCount || 0
        }));
      }

      // Load tournaments
      const { data: tournamentsData, error: tournamentsError } = await supabase
        .from('tournaments')
        .select('*')
        .eq('game_id', gameIdToLoad)
        .order('start_time', { ascending: false })
        .limit(10);

      if (tournamentsError) throw tournamentsError;
      setTournaments(tournamentsData || []);

      const activeTournamentCount = tournamentsData
        ? tournamentsData.filter(t => t.status === 'active' || t.status === 'pending').length
        : 0;

      setStats(prev => ({
        ...prev,
        activeTournaments: activeTournamentCount
      }));

      // Load anti-cheat flags
      const { data: flagsData, error: flagsError } = await supabase
        .from('anti_cheat_flags')
        .select(`
          *,
          user_profiles!inner(username, display_name),
          leaderboard_entries!inner(score)
        `)
        .eq('game_id', gameIdToLoad)
        .eq('reviewed', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (flagsError) throw flagsError;
      setAntiCheatFlags(flagsData || []);

      // Load achievements
      const { data: achievementsData, error: achievementsError} = await supabase
        .from('achievements')
        .select('*')
        .eq('game_id', gameIdToLoad)
        .order('created_at', { ascending: false });

      if (achievementsError) throw achievementsError;

      // Load achievement stats
      if (achievementsData && achievementsData.length > 0) {
        const achievementIds = achievementsData.map(a => a.id);

        const { data: stats, error: statsError } = await supabase
          .from('achievement_stats')
          .select('*')
          .in('achievement_id', achievementIds);

        if (statsError) throw statsError;

        // Create stats map
        const statsMap = {};
        if (stats) {
          stats.forEach(s => {
            statsMap[s.achievement_id] = s;
          });
        }

        // Enrich achievements with stats
        const enrichedAchievements = achievementsData.map(achievement => {
          const stat = statsMap[achievement.id] || {
            unlock_count: 0,
            unlock_rate_percent: 0,
            rarity: 'common'
          };
          return {
            ...achievement,
            unlockCount: stat.unlock_count,
            unlockRate: stat.unlock_rate_percent,
            rarity: stat.rarity
          };
        });

        // Calculate overall stats
        const totalUnlocks = enrichedAchievements.reduce((sum, a) => sum + (a.unlockCount || 0), 0);
        const averageUnlockRate = enrichedAchievements.length > 0
          ? enrichedAchievements.reduce((sum, a) => sum + (a.unlockRate || 0), 0) / enrichedAchievements.length
          : 0;

        const mostPopular = enrichedAchievements.length > 0
          ? enrichedAchievements.reduce((max, a) => (a.unlockRate > (max.unlockRate || 0) ? a : max), enrichedAchievements[0])
          : null;

        const rarest = enrichedAchievements.length > 0
          ? enrichedAchievements.reduce((min, a) => (a.unlockRate < (min.unlockRate || 100) ? a : min), enrichedAchievements[0])
          : null;

        setAchievements(enrichedAchievements);
        setAchievementStats({
          totalAchievements: enrichedAchievements.length,
          totalUnlocks,
          averageUnlockRate,
          mostPopular,
          rarest
        });
      }
    } catch (error) {
      console.error('Error loading game social data:', error);
    }
  };

  const handleGameChange = (game) => {
    setSelectedGame(game);
    navigate(`/studio/${studioId}/social/${game.id}`);
    loadGameSocialData(game.id);
  };

  const handleReviewFlag = async (flagId, action) => {
    try {
      const { error } = await supabase
        .from('anti_cheat_flags')
        .update({
          reviewed: true,
          reviewer_id: user.id,
          review_action: action,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', flagId);

      if (error) throw error;

      // Reload flags
      if (selectedGame) {
        await loadGameSocialData(selectedGame.id);
      }
    } catch (error) {
      console.error('Error reviewing flag:', error);
      alert('Failed to review flag');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading social dashboard...</p>
        </div>
      </div>
    );
  }

  if (!studio || games.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">No Games Found</h2>
          <p className="mt-2 text-gray-600">
            Publish a game to view social analytics
          </p>
          <button
            onClick={() => navigate(`/studio/${studioId}`)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Studio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Social Dashboard
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {studio.name} • Leaderboards, Tournaments & Anti-Cheat
              </p>
            </div>
            <button
              onClick={() => navigate(`/studio/${studioId}`)}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              ← Back to Studio
            </button>
          </div>

          {/* Game Selector */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Game
            </label>
            <select
              value={selectedGame?.id || ''}
              onChange={(e) => {
                const game = games.find(g => g.id === e.target.value);
                if (game) handleGameChange(game);
              }}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              {games.map(game => (
                <option key={game.id} value={game.id}>
                  {game.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {['overview', 'leaderboards', 'tournaments', 'achievements', 'anticheat'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} leaderboards={leaderboards} tournaments={tournaments} />
        )}
        {activeTab === 'leaderboards' && (
          <LeaderboardsTab leaderboards={leaderboards} gameId={selectedGame?.id} supabase={supabase} />
        )}
        {activeTab === 'tournaments' && (
          <TournamentsTab tournaments={tournaments} gameId={selectedGame?.id} supabase={supabase} />
        )}
        {activeTab === 'achievements' && (
          <AchievementsTab achievements={achievements} stats={achievementStats} />
        )}
        {activeTab === 'anticheat' && (
          <AntiCheatTab flags={antiCheatFlags} onReview={handleReviewFlag} />
        )}
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ stats, leaderboards, tournaments }) {
  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Submissions"
          value={stats.totalSubmissions.toLocaleString()}
          icon="📊"
          color="blue"
        />
        <StatCard
          title="Unique Players"
          value={stats.totalPlayers.toLocaleString()}
          icon="👥"
          color="green"
        />
        <StatCard
          title="Active Tournaments"
          value={stats.activeTournaments}
          icon="🏆"
          color="purple"
        />
        <StatCard
          title="Flagged Entries"
          value={stats.flaggedEntries}
          icon="⚠️"
          color="red"
        />
      </div>

      {/* Quick Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Leaderboards ({leaderboards.length})
          </h3>
          {leaderboards.length === 0 ? (
            <p className="text-gray-500">No leaderboards yet. Scores will auto-create leaderboards.</p>
          ) : (
            <div className="space-y-2">
              {leaderboards.slice(0, 5).map(lb => (
                <div key={lb.id} className="flex items-center justify-between py-2 border-b">
                  <span className="font-medium">{lb.name}</span>
                  <span className="text-sm text-gray-500">{lb.scope}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Tournaments ({tournaments.length})
          </h3>
          {tournaments.length === 0 ? (
            <p className="text-gray-500">No tournaments created yet.</p>
          ) : (
            <div className="space-y-2">
              {tournaments.slice(0, 5).map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b">
                  <span className="font-medium">{t.name}</span>
                  <span className={`text-sm px-2 py-1 rounded ${
                    t.status === 'active' ? 'bg-green-100 text-green-800' :
                    t.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    t.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Leaderboards Tab
function LeaderboardsTab({ leaderboards, gameId, supabase }) {
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLeaderboardEntries = async (leaderboardId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('leaderboard_entries')
        .select(`
          *,
          user_profiles(username, display_name)
        `)
        .eq('leaderboard_id', leaderboardId)
        .order('rank', { ascending: true })
        .limit(100);

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error loading entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLeaderboard) {
      loadLeaderboardEntries(selectedLeaderboard.id);
    }
  }, [selectedLeaderboard]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Leaderboards ({leaderboards.length})
        </h3>
        {leaderboards.length === 0 ? (
          <p className="text-gray-500">No leaderboards yet. They will be auto-created when players submit scores.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {leaderboards.map(lb => (
              <button
                key={lb.id}
                onClick={() => setSelectedLeaderboard(lb)}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  selectedLeaderboard?.id === lb.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <h4 className="font-semibold text-gray-900">{lb.name}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {lb.leaderboard_key} • {lb.scope}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Sort: {lb.sort_order}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedLeaderboard && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top 100 - {selectedLeaderboard.name}
          </h3>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : entries.length === 0 ? (
            <p className="text-gray-500">No entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map(entry => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{entry.rank}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.user_profiles?.display_name || entry.user_profiles?.username || 'Anonymous'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {parseFloat(entry.score).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(entry.submitted_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {entry.flagged_for_review ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Flagged
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Valid
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Tournaments Tab
function TournamentsTab({ tournaments, gameId, supabase }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Tournaments ({tournaments.length})
          </h3>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Create Tournament
          </button>
        </div>
        {tournaments.length === 0 ? (
          <p className="text-gray-500">No tournaments yet. Create your first tournament!</p>
        ) : (
          <div className="space-y-4">
            {tournaments.map(tournament => (
              <TournamentCard key={tournament.id} tournament={tournament} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Tournament Card
function TournamentCard({ tournament }) {
  return (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">{tournament.name}</h4>
          <p className="text-sm text-gray-500 mt-1">{tournament.description}</p>
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
            <span>🏆 {tournament.bracket_type}</span>
            <span>👥 {tournament.max_participants} max</span>
            {tournament.entry_fee_coins > 0 && (
              <span>🪙 {tournament.entry_fee_coins} entry</span>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${
          tournament.status === 'active' ? 'bg-green-100 text-green-800' :
          tournament.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          tournament.status === 'completed' ? 'bg-gray-100 text-gray-800' :
          'bg-red-100 text-red-800'
        }`}>
          {tournament.status}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <span>
          Start: {new Date(tournament.start_time).toLocaleDateString()}
        </span>
        <span>
          End: {new Date(tournament.end_time).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

// Anti-Cheat Tab
function AntiCheatTab({ flags, onReview }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Anti-Cheat Flags ({flags.length})
        </h3>
        {flags.length === 0 ? (
          <p className="text-gray-500">No suspicious activity detected. Great job!</p>
        ) : (
          <div className="space-y-4">
            {flags.map(flag => (
              <FlagCard key={flag.id} flag={flag} onReview={onReview} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Flag Card
function FlagCard({ flag, onReview }) {
  return (
    <div className={`border-l-4 rounded-lg p-4 ${
      flag.severity === 'critical' ? 'border-red-500 bg-red-50' :
      flag.severity === 'high' ? 'border-orange-500 bg-orange-50' :
      flag.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' :
      'border-gray-500 bg-gray-50'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              flag.severity === 'critical' ? 'bg-red-100 text-red-800' :
              flag.severity === 'high' ? 'bg-orange-100 text-orange-800' :
              flag.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {flag.severity.toUpperCase()}
            </span>
            <span className="text-sm font-medium text-gray-900">{flag.flag_type}</span>
          </div>
          <p className="text-sm text-gray-700 mt-2">{flag.reason}</p>
          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
            <span>Player: {flag.user_profiles?.username || 'Anonymous'}</span>
            <span>Score: {flag.leaderboard_entries?.score}</span>
            <span>{new Date(flag.created_at).toLocaleString()}</span>
          </div>
          {flag.details && (
            <details className="mt-2">
              <summary className="text-xs text-gray-600 cursor-pointer">View Details</summary>
              <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-x-auto">
                {JSON.stringify(flag.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onReview(flag.id, 'approved')}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          >
            Approve
          </button>
          <button
            onClick={() => onReview(flag.id, 'rejected')}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reject
          </button>
          <button
            onClick={() => onReview(flag.id, 'banned')}
            className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Ban User
          </button>
        </div>
      </div>
    </div>
  );
}

// Achievements Tab
function AchievementsTab({ achievements, stats }) {
  const getRarityColor = (rarity) => {
    switch(rarity) {
      case 'rare': return 'text-purple-600';
      case 'uncommon': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getRarityBadgeColor = (rarity) => {
    switch(rarity) {
      case 'rare': return 'bg-purple-100 text-purple-800';
      case 'uncommon': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Achievements"
          value={stats.totalAchievements}
          icon="⭐"
          color="blue"
        />
        <StatCard
          title="Total Unlocks"
          value={stats.totalUnlocks.toLocaleString()}
          icon="🔓"
          color="green"
        />
        <StatCard
          title="Avg Unlock Rate"
          value={`${stats.averageUnlockRate.toFixed(1)}%`}
          icon="📊"
          color="purple"
        />
        <StatCard
          title="Rarest Achievement"
          value={stats.rarest ? `${stats.rarest.unlockRate.toFixed(1)}%` : 'N/A'}
          icon="💎"
          color="purple"
        />
      </div>

      {/* Most Popular Achievement */}
      {stats.mostPopular && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">🏆</span>
            Most Popular Achievement
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-bold text-gray-900">{stats.mostPopular.name}</h4>
              <p className="text-sm text-gray-600 mt-1">{stats.mostPopular.description}</p>
              <p className="text-sm text-gray-500 mt-2">XP: {stats.mostPopular.xp_reward}</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-green-600">{stats.mostPopular.unlockRate.toFixed(1)}%</p>
              <p className="text-sm text-gray-500">{stats.mostPopular.unlockCount} unlocks</p>
            </div>
          </div>
        </div>
      )}

      {/* Achievements List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          All Achievements ({achievements.length})
        </h3>
        {achievements.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-4xl mb-4">⭐</p>
            <p className="text-gray-500 mb-2">No achievements defined yet</p>
            <p className="text-sm text-gray-400">
              Define achievements in your game using the SDK
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="bg-amber-50 rounded-lg p-3">
                    <span className="text-2xl">⭐</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{achievement.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRarityBadgeColor(achievement.rarity)}`}>
                        {achievement.rarity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      XP: {achievement.xp_reward}
                      {achievement.is_rare && <span className="ml-2 text-purple-600">✨ Rare</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className={`text-2xl font-bold ${getRarityColor(achievement.rarity)}`}>
                    {achievement.unlockRate.toFixed(1)}%
                  </p>
                  <p className="text-sm text-gray-500">{achievement.unlockCount} unlocks</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`text-4xl ${colorClasses[color] || colorClasses.blue} rounded-full p-3`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
